import React, { useEffect, useState } from "react";
import smooth from "../../Assets/Dashboard/smooth.png";
import match from "../../Assets/Dashboard/match.png";
import rain from "../../Assets/Dashboard/rain.png";
import random from "../../Assets/Dashboard/random.png";
import rapid from "../../Assets/Dashboard/rapid.png";
import ping from "../../Assets/Dashboard/ping.png";
import emoji from "../../Assets/Game/emoji.svg";
import glass from "../../Assets/Game/blueGass.svg";
import questsearch from "../../Assets/Dashboard/quest-search.png";
import RockChart from "../../Assets/Dashboard/RockChart.png";
import TheShooter from "../../Assets/Dashboard/Shooter.png";
import done from "../../Assets/Game/done.png";

import ModalSmoothMovement from "../Modal/ModalSmoothMovement";
import ModalRapidMovement from "../Modal/ModalRapidMovement";
import ModalPingPong from "../Modal/ModalPingPong";
import ModalMatchGabor from "../Modal/ModalMatchGabor";
import ModalRandomObstacle from "../Modal/ModalRandomObstacle";
import ModalRainDrop from "../Modal/ModalRainDrop";
import ModalSearchQuest from "../Modal/ModalSearchQuest";
import ModalTheShooter from "../Modal/ModalTheShooter";
import ModalRockChart from "../Modal/ModalRockChart";

import AuthUser from "../../Hooks/authUser";
import { useQuery } from "../../Hooks/useApi";
import moment from "moment";
import BlueRedSmoothMovementModal from "../Modal/BlueRedModal/BlueRedSmoothMovementModal";
import BlueRedRapidMovementModal from "../Modal/BlueRedModal/BlueRedRapidMovementModal";
import BlueRedPingPongModal from "../Modal/BlueRedModal/BlueRedPingPongModal";
import BlueRedRainDropModal from "../Modal/BlueRedModal/BlueRedRainDropModal";
import BlueRedRockChartModal from "../Modal/BlueRedModal/BlueRedRockChartModal";
import Loading from "../../Shared/Loading";
import BlueRedSearchQuestModal from "../Modal/BlueRedModal/BlueRedSearchQuestModal";
import BlueRedTheShooterModal from "../Modal/BlueRedModal/BlueRedTheShooterModal";
import BlueRedRandomObstacleModal from "../Modal/BlueRedModal/BlueRedRandomObstacleModal";
import { Icon } from "@iconify/react";

const UserDashboardGameCard = () => {
  const [loading, setLoading] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // { gameId, type }

  // Unified game scores state
  const [gameScores, setGameScores] = useState({
    smoothData: [],
    rapidData: [],
    pingPongData: [],
    rainDropData: [],
    matchGaborData: [],
    randomObstacleData: [],
    searchQuestData: [],
    rockChartData: [],
    theShooterData: [],
    colorTrapData: [],

    redBlueSmoothData: [],
    redBlueRapidData: [],
    redBluePingPongData: [],
    redBlueRainDropData: [],
    redBlueMatchGaborData: [],
    redBlueRandomObstacleData: [],
    redBlueSearchQuestData: [],
    redBlueRockChartData: [],
    redBlueTheShooterData: [],
    redBlueColorTrapData: [],
  });

  const { userInfo } = AuthUser();
  const date = moment().format("YYYY-MM-D");
  const currentDate = moment();

  // Rotation logic: odd days show odd indices, even days show even indices
  const show = Number(currentDate.format("DD") % 2);

  const { data: summaryData, loading: summaryLoading } = useQuery(
    userInfo?._id ? `/games/dashboard-summary?userId=${userInfo?._id}&date=${date}` : null
  );

  useEffect(() => {
    setLoading(summaryLoading);
  }, [summaryLoading]);

  useEffect(() => {
    if (summaryData?.status === "success" && Array.isArray(summaryData?.data)) {
      const gameRecords = summaryData.data;

      // Clear/Reset all values
      const newScores = {
        smoothData: [],
        rapidData: [],
        pingPongData: [],
        rainDropData: [],
        matchGaborData: [],
        randomObstacleData: [],
        searchQuestData: [],
        rockChartData: [],
        theShooterData: [],
        colorTrapData: [],
        redBlueSmoothData: [],
        redBlueRapidData: [],
        redBluePingPongData: [],
        redBlueRainDropData: [],
        redBlueMatchGaborData: [],
        redBlueRandomObstacleData: [],
        redBlueSearchQuestData: [],
        redBlueRockChartData: [],
        redBlueTheShooterData: [],
        redBlueColorTrapData: [],
      };

      // Map dynamic game records to state
      gameRecords.forEach((record) => {
        const isBinocular = record.gameName && record.gameName.includes("Binocular");
        const keyMap = {
          smoothMovement: isBinocular ? "redBlueSmoothData" : "smoothData",
          rapidMovement: isBinocular ? "redBlueRapidData" : "rapidData",
          pingPong: isBinocular ? "redBluePingPongData" : "pingPongData",
          rainDrop: isBinocular ? "redBlueRainDropData" : "rainDropData",
          matchGabor: isBinocular ? "redBlueMatchGaborData" : "matchGaborData",
          randomObstacle: isBinocular ? "redBlueRandomObstacleData" : "randomObstacleData",
          searchQuest: isBinocular ? "redBlueSearchQuestData" : "searchQuestData",
          rockChart: isBinocular ? "redBlueRockChartData" : "rockChartData",
          theShooter: isBinocular ? "redBlueTheShooterData" : "theShooterData",
          colorTrap: isBinocular ? "redBlueColorTrapData" : "colorTrapData",
        };

        const key = keyMap[record.gameId];
        if (key) {
          newScores[key] = [record];
        }
      });

      setGameScores(newScores);
    }
  }, [summaryData]);

  // Rotational games list
  const rotationalGames = [
    {
      id: "smoothMovement",
      name: "Smooth Movement",
      icon: smooth,
      normalKey: "smoothData",
      redBlueKey: "redBlueSmoothData",
    },
    {
      id: "rapidMovement",
      name: "Rapid Movement",
      icon: rapid,
      normalKey: "rapidData",
      redBlueKey: "redBlueRapidData",
    },
    {
      id: "pingPong",
      name: "Ping Pong",
      icon: ping,
      normalKey: "pingPongData",
      redBlueKey: "redBluePingPongData",
    },
    {
      id: "rockChart",
      name: "Rock Chart",
      icon: RockChart,
      normalKey: "rockChartData",
      redBlueKey: "redBlueRockChartData",
    },
    {
      id: "randomObstacle",
      name: "Random Obstacle",
      icon: random,
      normalKey: "randomObstacleData",
      redBlueKey: "redBlueRandomObstacleData",
    },
    {
      id: "searchQuest",
      name: "Search Quest",
      icon: questsearch,
      normalKey: "searchQuestData",
      redBlueKey: "redBlueSearchQuestData",
    },
  ];

  // Filter rotational games by odd/even index based on the day
  const filteredRotational = rotationalGames.filter((_, i) => {
    return show !== 0 ? i % 2 !== 0 : i % 2 === 0;
  });

  // Assemble absolute list of active games to render
  const activeGames = [
    {
      id: "theShooter",
      name: "The Shooter",
      icon: TheShooter,
      normalKey: "theShooterData",
      redBlueKey: "redBlueTheShooterData",
    },
    {
      id: "rainDrop",
      name: "Rain Drop",
      icon: rain,
      normalKey: "rainDropData",
      redBlueKey: "redBlueRainDropData",
    },
    ...filteredRotational,
    {
      id: "matchGabor",
      name: "Match Gabor",
      icon: match,
      normalKey: "matchGaborData",
      redBlueKey: null,
    },
  ];

  // Helper to format seconds to M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Helper to get played time for a mode
  const getModePlayedTime = (data, totalDuration) => {
    if (!data || !data.length) return 0;
    const remaining = data[0]?.remainingTime;
    if (remaining === undefined || remaining === null) return 0;
    const effectiveRemaining = remaining < 10 ? 0 : remaining;
    return Math.max(0, totalDuration - effectiveRemaining);
  };

  // Helper to get aggregated playtime stats for a card
  const getCardTimeStats = (game) => {
    const isMatchGabor = game.id === "matchGabor";
    const normalTotal = isMatchGabor ? 600 : 300;
    const normalPlayed = getModePlayedTime(gameScores[game.normalKey], normalTotal);

    let redBlueTotal = 0;
    let redBluePlayed = 0;
    if (game.redBlueKey) {
      redBlueTotal = 300;
      redBluePlayed = getModePlayedTime(gameScores[game.redBlueKey], redBlueTotal);
    }

    return {
      played: normalPlayed + redBluePlayed,
      total: normalTotal + redBlueTotal,
    };
  };

  // Progress calculator
  const getProgress = (data) => {
    if (!data || !data.length) return 0;
    let remainingTime = data[0]?.remainingTime;
    if (remainingTime === undefined || remainingTime === null) return 0;
    const isMatchGabor = data[0]?.gameId === "matchGabor" || data[0]?.gameName?.includes("Match Gabor");
    const gameDuration = isMatchGabor ? 600 : 300;
    if (remainingTime < 10) remainingTime = 0;
    return Math.min(100, Math.max(0, Math.ceil(((gameDuration - remainingTime) / gameDuration) * 100)));
  };

  // Check if a game mode is fully completed
  const isCompleted = (data) => {
    if (!data || !data.length) return false;
    return data[0]?.remainingTime < 10;
  };

  const isPlayDisabled = (data) => {
    if (!data || !data.length) return false;
    return data[0]?.remainingTime < 10;
  };

  const getButtonContent = (data, defaultIcon) => {
    const remaining = data?.[0]?.remainingTime;
    if (remaining === undefined || remaining === null || data.length === 0 || remaining >= 10) {
      return <img className="w-5 h-5 object-contain" src={defaultIcon} alt="play icon" />;
    }
    return <img className="w-5 h-5 object-contain" src={done} alt="completed icon" />;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6 my-6 select-none">
      {/* Title section */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-800">Visual Therapy Tasks</h2>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Complete your prescribed exercises for today</p>
        </div>
        <div className="flex items-center gap-2 px-3.5 py-2 bg-[#291D89]/5 rounded-xl border border-[#291D89]/10 text-xs text-[#291D89] font-bold">
          <Icon icon="solar:info-circle-bold" className="text-base" />
          <span>Rotation Set: {show !== 0 ? "Odd Days Plan" : "Even Days Plan"}</span>
        </div>
      </div>

      {/* Grid of game cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activeGames.map((game) => (
          <div
            key={game.id}
            className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
          >
            {/* Header info */}
            <div className="flex items-center justify-between border-b border-slate-55 pb-4 gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center p-2 flex-shrink-0">
                  <img className="object-contain max-h-full max-w-full" src={game.icon} alt={game.name} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-slate-800 tracking-tight truncate">{game.name}</h3>
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Exercise Therapy</p>
                </div>
              </div>
              <div className="flex-shrink-0">
                {(() => {
                  const stats = getCardTimeStats(game);
                  return (
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-[#291D89]/5 rounded-lg border border-[#291D89]/10">
                      <Icon icon="solar:stopwatch-bold" className="text-[#291D89] text-[13px]" />
                      <span className="text-[10px] font-bold text-[#291D89] whitespace-nowrap">
                        {formatTime(stats.played)} / {formatTime(stats.total)} min
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Modes container */}
            <div className="grid grid-cols-2 gap-4 mt-5">
              {/* Red-Blue mode */}
              {game.redBlueKey ? (
                <div className="flex flex-col justify-between h-full bg-slate-50/50 rounded-xl p-3 border border-slate-100/50">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
                      <span>Red-Blue ({formatTime(getModePlayedTime(gameScores[game.redBlueKey], 300))}/5:00)</span>
                      <span>{getProgress(gameScores[game.redBlueKey])}%</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-secondary rounded-full transition-all duration-300"
                        style={{ width: `${getProgress(gameScores[game.redBlueKey])}%` }}
                      ></div>
                    </div>
                  </div>
                  <button
                    disabled={isPlayDisabled(gameScores[game.redBlueKey])}
                    onClick={() => setActiveModal({ gameId: game.id, type: "redBlue" })}
                    className="mt-3.5 w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl border border-slate-200 hover:border-secondary hover:bg-[#291D89]/5 text-slate-600 hover:text-[#291D89] font-bold text-xs transition duration-200 disabled:opacity-50 disabled:bg-slate-50 disabled:hover:border-slate-200 disabled:hover:bg-transparent"
                  >
                    {getButtonContent(gameScores[game.redBlueKey], glass)}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col justify-center items-center h-full bg-slate-50/20 rounded-xl p-3 border border-dashed border-slate-200/60 min-h-[92px]">
                  <span className="text-[10px] font-bold text-slate-400 text-center">No Red-Blue Mode</span>
                </div>
              )}

              {/* Normal mode */}
              <div className="flex flex-col justify-between h-full bg-slate-50/50 rounded-xl p-3 border border-slate-100/50">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
                    <span>Normal ({formatTime(getModePlayedTime(gameScores[game.normalKey], game.id === "matchGabor" ? 600 : 300))}/{game.id === "matchGabor" ? "10:00" : "5:00"})</span>
                    <span>{getProgress(gameScores[game.normalKey])}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary rounded-full transition-all duration-300"
                      style={{ width: `${getProgress(gameScores[game.normalKey])}%` }}
                    ></div>
                  </div>
                </div>
                <button
                  disabled={isPlayDisabled(gameScores[game.normalKey])}
                  onClick={() => setActiveModal({ gameId: game.id, type: "normal" })}
                  className="mt-3.5 w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-[#291D89] hover:bg-[#291D89]/90 text-white font-bold text-xs transition duration-200 disabled:opacity-50 disabled:bg-slate-55 disabled:text-slate-400"
                >
                  {game.id === "matchGabor" ? (
                    isCompleted(gameScores[game.normalKey]) ? (
                      <img className="w-5 h-5 object-contain" src={done} alt="done" />
                    ) : (
                      <Icon className="text-lg text-white" icon="ph:play-bold" />
                    )
                  ) : (
                    getButtonContent(gameScores[game.normalKey], emoji)
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dynamic Modal Mounting */}
      {activeModal?.gameId === "smoothMovement" && activeModal?.type === "normal" && (
        <ModalSmoothMovement isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "smoothMovement" && activeModal?.type === "redBlue" && (
        <BlueRedSmoothMovementModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "rapidMovement" && activeModal?.type === "normal" && (
        <ModalRapidMovement isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "rapidMovement" && activeModal?.type === "redBlue" && (
        <BlueRedRapidMovementModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "pingPong" && activeModal?.type === "normal" && (
        <ModalPingPong isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "pingPong" && activeModal?.type === "redBlue" && (
        <BlueRedPingPongModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "rainDrop" && activeModal?.type === "normal" && (
        <ModalRainDrop isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "rainDrop" && activeModal?.type === "redBlue" && (
        <BlueRedRainDropModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "matchGabor" && activeModal?.type === "normal" && (
        <ModalMatchGabor isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "randomObstacle" && activeModal?.type === "normal" && (
        <ModalRandomObstacle isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "randomObstacle" && activeModal?.type === "redBlue" && (
        <BlueRedRandomObstacleModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "searchQuest" && activeModal?.type === "normal" && (
        <ModalSearchQuest isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "searchQuest" && activeModal?.type === "redBlue" && (
        <BlueRedSearchQuestModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "rockChart" && activeModal?.type === "normal" && (
        <ModalRockChart isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "rockChart" && activeModal?.type === "redBlue" && (
        <BlueRedRockChartModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}

      {activeModal?.gameId === "theShooter" && activeModal?.type === "normal" && (
        <ModalTheShooter isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
      {activeModal?.gameId === "theShooter" && activeModal?.type === "redBlue" && (
        <BlueRedTheShooterModal isOpen={true} onRequestClose={() => setActiveModal(null)} />
      )}
    </div>
  );
};

export default UserDashboardGameCard;
